// src/pages/equipamentos/DialogEquipamentoEventos.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import Draggable from "react-draggable";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import { createAutocompleteFilter } from "pages/utils/autocompleteFilter";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TodayIcon from "@mui/icons-material/Today";
import DeleteIcon from "@mui/icons-material/Delete";

import DialogEquipamentoEventoLinhas from "pages/equipamentos/DialogEquipamentoEventoLinhas";
import DialogEquipamentoFicheiros from "pages/equipamentos/DialogEquipamentoFicheiros";

function PaperComponent(props) {
  return (
    <Draggable handle=".dialog-drag-handle" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper
        {...props}
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          opacity: 1,
        }}
      />
    </Draggable>
  );
}

// helpers duração
const qhToHHmm = (qh) => {
  const n = Number(qh ?? 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  const totalMin = n * 15;
  const hh = String(Math.floor(totalMin / 60)).padStart(2, "0");
  const mm = String(totalMin % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const hhmmToHours = (s) => {
  if (!s) return 0;
  const m = String(s).match(/^(\d{1,2}):([0-5]\d)$/);
  if (!m) return 0;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  return hh + mm / 60;
};

const fmtMoney = (v) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const calcLinhaTotal = (ln) => {
  const q = Number(ln?.quantidade ?? 0);
  const pu = Number(ln?.preco_unitario ?? 0);

  if (!Number.isFinite(q) || !Number.isFinite(pu)) return 0;
  return q * pu;
};

const hhmmToMinutes = (s) => {
  if (!s) return 0;
  const m = String(s).match(/^(\d{1,2}):([0-5]\d)$/);
  if (!m) return 0;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  return hh * 60 + mm;
};

const qhToMinutes = (qh) => {
  const n = Number(qh ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n * 15;
};

const minutesToHHmm = (min) => {
  const n = Number(min ?? 0);
  if (!Number.isFinite(n) || n <= 0) return "00:00";
  const hh = String(Math.floor(n / 60)).padStart(2, "0");
  const mm = String(n % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const fmtConsumoHistorico100 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtKmsCalc = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const fmtLitrosCalc = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const calcularConsumoHistorico = (rows) => {
  const eventos = (Array.isArray(rows) ? rows : [])
    .filter((ev) => Number(ev?.id_tipo_evento ?? 0) === 6)
    .map((ev) => ({
      id: Number(ev?.id ?? 0),
      data_evento: ev?.data_evento ? String(ev.data_evento).slice(0, 10) : "",
      kms: Number(ev?.kms_atuais ?? 0),
      qtd: Number(ev?.quantidade_linhas ?? 0),
    }))
    .filter(
      (x) =>
        x.data_evento && Number.isFinite(x.kms) && x.kms > 0 && Number.isFinite(x.qtd) && x.qtd > 0
    )
    .sort((a, b) => {
      const cmpData = String(a.data_evento).localeCompare(String(b.data_evento));
      if (cmpData !== 0) return cmpData;
      return Number(a.id ?? 0) - Number(b.id ?? 0);
    });

  if (eventos.length < 2) return null;

  const primeiro = eventos[0];
  const ultimo = eventos[eventos.length - 1];
  const penultimo = eventos.length >= 2 ? eventos[eventos.length - 2] : null;

  if (!primeiro || !ultimo || !penultimo) return null;

  if (!(Number(ultimo.kms ?? 0) > Number(penultimo.kms ?? 0))) return null;

  const kmsInicio = Number(primeiro.kms ?? 0);
  const kmsFim = Number(ultimo.kms ?? 0);
  const diff = kmsFim - kmsInicio;

  if (!Number.isFinite(diff) || diff <= 0) return null;

  const quantidadeTotal = eventos.slice(0, -1).reduce((acc, x) => {
    const q = Number(x?.qtd ?? 0);
    return acc + (Number.isFinite(q) ? q : 0);
  }, 0);

  if (!Number.isFinite(quantidadeTotal) || quantidadeTotal <= 0) return null;

  return {
    kmsInicio,
    kmsFim,
    kmsTotal: diff,
    quantidadeTotal,
    consumo100: (quantidadeTotal / diff) * 100,
  };
};

const getMesKey = (v) => {
  if (!v) return "";
  const s = String(v).slice(0, 10);
  if (s.length < 7) return "";
  return s.slice(0, 7); // YYYY-MM
};

const fmtConsumo100 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ------------------- Lista de Durações (00:15 → 99:45) -------------------
const DURACOES = (() => {
  const arr = [];
  for (let h = 0; h <= 99; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 0 && m === 0) continue; // não queremos "00:00"
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      arr.push(`${hh}:${mm}`);
    }
  }
  return arr;
})();

const isValidHHmm = (s) => {
  if (!s) return true;
  const m = String(s).match(/^(\d{1,2}):([0-5]\d)$/);
  if (!m) return false;
  const minutes = Number(m[2]);
  return minutes % 15 === 0;
};

export default function DialogEquipamentoEventos({
  open,
  onClose,
  equipamento,
  idEventoAbrir = 0,
}) {
  const abriuDeepLinkRef = useRef(false);

  const { showAlert } = useAlert();
  const { temPermissao, userData } = useAuth();

  const idEquipamento = Number(equipamento?.id || 0);

  // tipo 1 = Viatura
  const idTipoEquipamento =
    Number(
      equipamento?.id_tipo_equipamento ??
        equipamento?.id_equipamento_tipo ??
        equipamento?.id_tipo ??
        equipamento?.idtipo ??
        0
    ) || 0;

  const isViatura =
    idTipoEquipamento === 1 ||
    String(equipamento?.tipo_nome_curto ?? equipamento?.tipo ?? equipamento?.nome_tipo ?? "")
      .toUpperCase()
      .includes("VIATURA");

  const [loading, setLoading] = useState(false);
  const [tiposEventos, setTiposEventos] = useState([]);
  const [data, setData] = useState([]);

  const ESTADOS_EVENTO = ["Não iniciado", "Em tratamento", "Concluído", "Cancelado"];

  // dialog detalhe / info do evento
  const [openInfo, setOpenInfo] = useState(false);
  const [eventoInfo, setEventoInfo] = useState(null);

  // dialog linhas do evento
  const [openLinhas, setOpenLinhas] = useState(false);
  const [eventoLinhas, setEventoLinhas] = useState(null);

  // dialog anexos do evento
  const [openAnexosEvento, setOpenAnexosEvento] = useState(false);

  const [openConfirmDel, setOpenConfirmDel] = useState(false);
  const [eventoParaApagar, setEventoParaApagar] = useState(null);

  const [linhasInfo, setLinhasInfo] = useState([]);
  const [loadingInfo, setLoadingInfo] = useState(false);

  const handleOpenInfo = async (ev) => {
    if (!ev?.id) return;

    setEventoInfo(ev);
    setOpenInfo(true);
    setLinhasInfo([]);
    setLoadingInfo(true);

    const [payload, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentoEventoLinhas", {
        id_evento: Number(ev.id),
      })
    );

    setLoadingInfo(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    setLinhasInfo(arr);
  };

  const handleCloseInfo = () => {
    setOpenInfo(false);
    setEventoInfo(null);
    setLinhasInfo([]);
    setLoadingInfo(false);
  };

  const handleOpenLinhas = (ev) => {
    if (!ev?.id) return;
    setEventoLinhas(ev);
    setOpenLinhas(true);
  };

  const handleCloseLinhas = () => {
    setOpenLinhas(false);
    setEventoLinhas(null);
  };

  const handleOpenAnexosEvento = () => {
    if (!Number(form?.id ?? 0)) return;
    setOpenAnexosEvento(true);
  };

  const emptyForm = {
    id: 0,
    id_equipamento: 0,
    id_tipo_evento: 0,
    titulo: "",
    descricao: "",

    // (1) realização do evento
    data_evento: "",
    data_limite: "",
    estado: "Não iniciado",

    duracao_h: "00:15",

    mao_obra_interna: 1,
    imobilizacao_h: "",
    custo_hora_imobilizacao: "",

    kms_atuais: "",

    // breakdown (vêm do SELECT)
    valor_linhas: "",
    valor_mao_obra: "",
    valor_imobilizacao: "",
    valor: "",
  };

  const [openEdit, setOpenEdit] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [estadoInput, setEstadoInput] = useState("");

  // Dialog "Marcar evento futuro?"
  const [openMarcarFuturo, setOpenMarcarFuturo] = useState(false);
  const [novoEventoData, setNovoEventoData] = useState("");
  const [novoEventoLimite, setNovoEventoLimite] = useState("");
  const [novoEventoKms, setNovoEventoKms] = useState("");
  const [eventoBaseFuturo, setEventoBaseFuturo] = useState(null);
  const [pendentePerguntarFuturo, setPendentePerguntarFuturo] = useState(false);

  const carregarTiposEventos = async () => {
    const [payloadTipos, errTipos] = await handleApi(
      api.post("Equipamentos/SelEquipamentosTiposEventos", { mostrar_inativos: 1 })
    );
    if (errTipos) showAlert(errTipos, "error");

    const teArr = Array.isArray(payloadTipos?.data)
      ? payloadTipos.data
      : Array.isArray(payloadTipos)
      ? payloadTipos
      : [];

    setTiposEventos(teArr);
  };

  const carregarEventos = async () => {
    if (!idEquipamento || idEquipamento <= 0) {
      setData([]);
      return;
    }

    setLoading(true);
    const [payload, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentoEventos", {
        id_equipamento: idEquipamento,
        mostrar_inativos: 1,
      })
    );
    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    setData(arr);
  };

  // quando abre, carrega base + lista
  useEffect(() => {
    if (!open) return;

    (async () => {
      await carregarTiposEventos();
      await carregarEventos();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idEquipamento]);

  useEffect(() => {
    if (!open) return;
    if (!idEventoAbrir || idEventoAbrir <= 0) return;
    if (abriuDeepLinkRef.current) return;
    if (!Array.isArray(data) || data.length === 0) return;

    const ev = data.find((x) => Number(x?.id) === Number(idEventoAbrir));
    if (!ev) return;

    abriuDeepLinkRef.current = true;
    handleOpenEditar(ev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idEventoAbrir, data]);

  const confirmarApagarEvento = async () => {
    if (!eventoParaApagar?.id) return;

    setLoading(true);
    const [_, err, meta] = await handleApi(
      api.post("Equipamentos/DelEquipamentoEvento", { id: eventoParaApagar.id })
    );
    setLoading(false);

    setOpenConfirmDel(false);
    setEventoParaApagar(null);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");
    await carregarEventos();
  };

  const handleOpenNovo = () => {
    if (!temPermissao("Menu Equipamentos", "w")) return;
    if (!idEquipamento) return showAlert("Equipamento inválido.", "warning");

    setEditando(false);

    setForm({
      ...emptyForm,
      id_equipamento: idEquipamento,
      id_tipo_evento: 0,

      // (1)
      data_evento: "",
      data_limite: "",
      estado: "Não iniciado",

      duracao_h: "00:15",

      custo_hora_imobilizacao:
        equipamento?.custo_hora_imobilizacao === null ||
        equipamento?.custo_hora_imobilizacao === undefined
          ? ""
          : String(equipamento.custo_hora_imobilizacao),
    });

    setEstadoInput("Não iniciado");
    setOpenEdit(true);
  };

  const handleOpenEditar = (ev) => {
    if (!temPermissao("Menu Equipamentos", "u")) return;

    setEditando(true);
    setForm({
      ...emptyForm,
      ...ev,

      id: ev.id,
      id_equipamento: ev.id_equipamento ?? idEquipamento,
      id_tipo_evento: ev.id_tipo_evento ?? 0,

      // (1) realização
      data_evento: ev.data_evento ? String(ev.data_evento).slice(0, 10) : "",
      data_limite: ev.data_limite ? String(ev.data_limite).slice(0, 10) : "",
      estado: ev.estado ?? "Não iniciado",

      duracao_h: ev.duracao_h ? String(ev.duracao_h) : qhToHHmm(ev.duracao_qh),

      mao_obra_interna:
        ev.mao_obra_interna === null || ev.mao_obra_interna === undefined
          ? 1
          : Number(ev.mao_obra_interna) === 1
          ? 1
          : 0,

      imobilizacao_h:
        ev.imobilizacao_h && ev.imobilizacao_h !== "00:00" ? String(ev.imobilizacao_h) : "",

      custo_hora_imobilizacao:
        ev.custo_hora_imobilizacao === null || ev.custo_hora_imobilizacao === undefined
          ? ""
          : String(ev.custo_hora_imobilizacao),

      kms_atuais: ev.kms_atuais ?? "",

      // breakdown
      valor_linhas: ev.valor_linhas ?? "",
      valor_mao_obra: ev.valor_mao_obra ?? "",
      valor_imobilizacao: ev.valor_imobilizacao ?? "",
      valor: ev.valor ?? "",
    });

    setEstadoInput(ev.estado ?? "Não iniciado");
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditando(false);
    setForm({ ...emptyForm });
    // NÃO mexer aqui no abriuDeepLinkRef, senão o deep-link reabre o dialog
  };

  const handleSave = async () => {
    const idEq = Number(form.id_equipamento || 0);
    const idTipo = Number(form.id_tipo_evento || 0);

    if (idEq <= 0) return showAlert("Equipamento obrigatório.", "warning");
    if (idTipo <= 0) return showAlert("Tipo de evento obrigatório.", "warning");

    if (form.duracao_h && !isValidHHmm(form.duracao_h))
      return showAlert(
        "Duração inválida. Use HH:mm (ex: 00:15) e minutos múltiplos de 15.",
        "warning"
      );

    if (form.imobilizacao_h && !isValidHHmm(form.imobilizacao_h))
      return showAlert(
        "Imobilização inválida. Use HH:mm (ex: 10:00) e minutos múltiplos de 15.",
        "warning"
      );

    let custoHoraImob = null;
    if (String(form.custo_hora_imobilizacao ?? "").trim() !== "") {
      const n = Number(String(form.custo_hora_imobilizacao).replace(",", "."));
      if (!Number.isFinite(n) || n < 0)
        return showAlert("Custo/hora imobilização inválido.", "warning");
      custoHoraImob = n;
    }

    const devePerguntarFuturo =
      editando &&
      (form.estado === "Concluído" || form.estado === "Cancelado") &&
      Number(form?.id ?? 0) > 0;

    setLoading(true);

    const body = {
      ...form,
      titulo: (form.titulo ?? "").toUpperCase(),
      descricao: (form.descricao ?? "").toUpperCase(),
      id_equipamento: idEq,
      id_tipo_evento: idTipo,

      mao_obra_interna: Number(form.mao_obra_interna ?? 0) === 1 ? 1 : 0,
      imobilizacao_h: form.imobilizacao_h ? String(form.imobilizacao_h) : null,
      custo_hora_imobilizacao: custoHoraImob,

      kms_atuais: form.kms_atuais === "" ? null : Number(form.kms_atuais),

      // não enviar breakdown como campos "de gravação"
      valor: null,
      valor_linhas: null,
      valor_mao_obra: null,
      valor_imobilizacao: null,

      data_evento: form.data_evento || null,
      data_limite: form.data_limite || null,

      id_utilizador: userData?.id ?? null,
    };

    const endpoint = editando
      ? "Equipamentos/UpdEquipamentoEvento"
      : "Equipamentos/InsEquipamentoEvento";

    const [payload, err, meta] = await handleApi(api.post(endpoint, body));
    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    // recarrega lista (para refletir imediatamente na tabela principal)
    await carregarEventos();

    if (!editando) {
      // ✅ INSERT: tenta obter o id devolvido pela API
      const novoId =
        Number(payload?.data?.id ?? payload?.id ?? payload?.data?.ID ?? payload?.ID ?? 0) || 0;

      if (novoId > 0) {
        // mantém o dialog aberto e passa a "modo edição"
        setEditando(true);

        setForm((p) => ({
          ...p,
          id: novoId,
        }));

        // abre logo as linhas do evento
        handleOpenLinhas({
          ...body,
          id: novoId,
          titulo: body?.titulo ?? "",
        });

        return; // não fecha o dialog
      }
    }

    // UPDATE (ou INSERT sem id): comportamento normal
    if (devePerguntarFuturo) {
      setEventoBaseFuturo({
        ...body,
        id: Number(form?.id ?? 0),
      });
      setNovoEventoData("");
      setNovoEventoLimite("");
      setNovoEventoKms("");
      setPendentePerguntarFuturo(true);
    }

    handleCloseEdit();
  };

  const fecharEvento = async (ev, estado) => {
    if (!temPermissao("Menu Equipamentos", "u")) return;

    setLoading(true);
    const [_, err, meta] = await handleApi(
      api.post("Equipamentos/FecharEquipamentoEvento", {
        id: ev.id,
        estado,
        id_utilizador: userData?.id ?? null,
      })
    );
    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");
    await carregarEventos();
  };

  const marcarEventoFuturo = async () => {
    if (!eventoBaseFuturo) return;

    if (!novoEventoData && String(novoEventoKms).trim() === "") {
      showAlert("Defina a nova data do evento ou os novos KMs.", "warning");
      return;
    }

    let kmsFuturo = null;
    if (String(novoEventoKms).trim() !== "") {
      const nKms = Number(String(novoEventoKms).replace(",", "."));
      if (!Number.isFinite(nKms) || nKms < 0 || !Number.isInteger(nKms)) {
        showAlert("KMs inválidos.", "warning");
        return;
      }
      kmsFuturo = nKms;
    }

    // replica a mesma lógica de normalização do handleSave
    let custoHoraImob = null;

    if (String(eventoBaseFuturo.custo_hora_imobilizacao ?? "").trim() !== "") {
      const n = Number(String(eventoBaseFuturo.custo_hora_imobilizacao).replace(",", "."));
      if (!Number.isFinite(n) || n < 0) {
        showAlert("Custo/hora imobilização inválido.", "warning");
        return;
      }
      custoHoraImob = n;
    }

    const body = {
      ...eventoBaseFuturo,

      // força INSERT
      id: 0,

      titulo: (eventoBaseFuturo.titulo ?? "").toUpperCase(),
      descricao: (eventoBaseFuturo.descricao ?? "").toUpperCase(),

      id_equipamento: Number(eventoBaseFuturo.id_equipamento || 0),
      id_tipo_evento: Number(eventoBaseFuturo.id_tipo_evento || 0),

      // novo agendamento
      data_evento: novoEventoData || null,
      data_limite: novoEventoLimite || null,

      // novo estado
      estado: "Não iniciado",

      // garante coerência
      mao_obra_interna: Number(eventoBaseFuturo.mao_obra_interna ?? 0) === 1 ? 1 : 0,
      imobilizacao_h: eventoBaseFuturo.imobilizacao_h
        ? String(eventoBaseFuturo.imobilizacao_h)
        : null,
      custo_hora_imobilizacao: custoHoraImob,
      kms_atuais:
        kmsFuturo !== null
          ? kmsFuturo
          : eventoBaseFuturo.kms_atuais === ""
          ? null
          : Number(eventoBaseFuturo.kms_atuais),

      // não gravar breakdown
      valor: null,
      valor_linhas: null,
      valor_mao_obra: null,
      valor_imobilizacao: null,

      // liga ao evento concluído/cancelado
      id_antecedente: Number(eventoBaseFuturo.id || 0),

      id_utilizador: userData?.id ?? null,
    };

    setLoading(true);
    const [payload, err, meta] = await handleApi(
      api.post("Equipamentos/InsEquipamentoEvento", body)
    );
    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "Evento futuro marcado.", meta?.severity ?? "success");

    setOpenMarcarFuturo(false);
    setEventoBaseFuturo(null);
    setNovoEventoData("");
    setNovoEventoLimite("");
    setNovoEventoKms("");

    await carregarEventos();

    // opcional: abrir logo o evento recém criado
    const novoId =
      Number(payload?.data?.id ?? payload?.id ?? payload?.data?.ID ?? payload?.ID ?? 0) || 0;
    if (novoId > 0) {
      const evNovo = {
        ...body,
        id: novoId,
        data_evento: body.data_evento,
        data_limite: body.data_limite,
      };
      handleOpenEditar(evNovo);
    }
  };

  const rowActions = useMemo(
    () => [
      {
        id: "info",
        icon: <InfoOutlinedIcon fontSize="small" />,
        tooltip: "Info",
        color: "info",
        onClick: (ev) => handleOpenInfo(ev),
      },
      {
        id: "editar",
        icon: <EditIcon fontSize="small" />,
        tooltip: "Editar",
        color: "primary",
        disabled: () => !temPermissao("Menu Equipamentos", "u"),
        onClick: (ev) => handleOpenEditar(ev),
      },
      {
        id: "concluir",
        icon: <CheckCircleIcon fontSize="small" />,
        tooltip: "Fechar como Concluído",
        disabled: (ev) => {
          const est = String(ev?.estado ?? "")
            .trim()
            .toLowerCase();

          const fechado = est === "concluído" || est === "concluido" || est === "cancelado";

          return fechado || !temPermissao("Menu Equipamentos", "u");
        },
        onClick: (ev) => fecharEvento(ev, "Concluído"),
      },
      {
        id: "apagar",
        icon: <DeleteIcon fontSize="small" />,
        tooltip: "Apagar",
        color: "error",
        disabled: (ev) => {
          const est = String(ev?.estado ?? "")
            .trim()
            .toLowerCase();

          const fechado = est === "concluído" || est === "concluido" || est === "cancelado";

          return fechado || !temPermissao("Menu Equipamentos", "d");
        },
        onClick: (ev) => {
          setEventoParaApagar(ev);
          setOpenConfirmDel(true);
        },
      },
    ],
    [temPermissao]
  );

  const columns = useMemo(() => {
    const isViatura = Number(equipamento?.id_tipo ?? 0) === 1;

    const cols = [
      {
        Header: "Data",
        accessor: "data_evento",
        Cell: ({ value }) => (value ? String(value).slice(0, 10) : ""),
      },
    ];

    // ✅ Só para Viatura: KMs atuais (antes) + KMs Agendam.
    if (isViatura) {
      cols.push({
        Header: "KMs",
        accessor: "kms_atuais",
        Cell: ({ value }) => (value === null || value === undefined ? "" : String(value)),
      });
    }

    // resto das colunas (sempre)
    cols.push(
      { Header: "Tipo", accessor: "tipo_evento_nome_curto" },
      { Header: "Título", accessor: "titulo" },
      { Header: "Estado", accessor: "estado" },
      {
        Header: "Duração Evento",
        accessor: "duracao_h",
        Cell: ({ value, row }) => (value ? String(value) : qhToHHmm(row.original?.duracao_qh)),
      },
      {
        Header: "Duração Imobilização",
        accessor: "imobilizacao_h",
        Cell: ({ value, row }) => {
          if (value) return String(value);
          return qhToHHmm(row.original?.imobilizacao_qh);
        },
      },
      {
        Header: "Valor",
        accessor: "valor",
        Cell: ({ value }) => fmtMoney(value),
      }
    );

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temPermissao, userData, open, equipamento?.id_tipo]);

  const consumoMensalMap = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];

    const eventosConsumo = arr
      .filter((ev) => Number(ev?.id_tipo_evento ?? 0) === 6)
      .map((ev) => ({
        id: Number(ev?.id ?? 0),
        data_evento: ev?.data_evento ? String(ev.data_evento).slice(0, 10) : "",
        mes: getMesKey(ev?.data_evento),
        kms: Number(ev?.kms_atuais ?? 0),
        qtd: Number(ev?.quantidade_linhas ?? 0),
      }))
      .filter(
        (x) =>
          x.data_evento &&
          x.mes &&
          Number.isFinite(x.kms) &&
          x.kms > 0 &&
          Number.isFinite(x.qtd) &&
          x.qtd >= 0
      )
      .sort((a, b) => {
        const cmpData = String(a.data_evento).localeCompare(String(b.data_evento));
        if (cmpData !== 0) return cmpData;
        return Number(a.id ?? 0) - Number(b.id ?? 0);
      });

    const mesesMap = {};
    eventosConsumo.forEach((ev) => {
      if (!mesesMap[ev.mes]) mesesMap[ev.mes] = [];
      mesesMap[ev.mes].push(ev);
    });

    const mesesOrdenados = Object.keys(mesesMap).sort((a, b) => a.localeCompare(b));
    const res = {};

    for (let i = 0; i < mesesOrdenados.length; i++) {
      const mes = mesesOrdenados[i];
      const eventos = Array.isArray(mesesMap[mes]) ? mesesMap[mes] : [];

      if (eventos.length === 0) {
        res[mes] = { consumo100: null };
        continue;
      }

      const ultima = eventos[eventos.length - 1];
      const penultima = eventos.length >= 2 ? eventos[eventos.length - 2] : null;

      if (!ultima) {
        res[mes] = { consumo100: null };
        continue;
      }

      if (penultima && !(Number(ultima.kms ?? 0) > Number(penultima.kms ?? 0))) {
        res[mes] = { consumo100: null };
        continue;
      }

      let kmsInicio = null;
      let quantidadeTotal = 0;

      if (i === 0) {
        const primeira = eventos[0];
        if (!primeira) {
          res[mes] = { consumo100: null };
          continue;
        }

        kmsInicio = Number(primeira.kms ?? 0);

        quantidadeTotal = eventos.slice(0, -1).reduce((acc, x) => {
          const q = Number(x?.qtd ?? 0);
          return acc + (Number.isFinite(q) ? q : 0);
        }, 0);
      } else {
        const mesAnterior = mesesOrdenados[i - 1];
        const eventosMesAnterior = Array.isArray(mesesMap[mesAnterior])
          ? mesesMap[mesAnterior]
          : [];
        const ultimoMesAnterior =
          eventosMesAnterior.length > 0 ? eventosMesAnterior[eventosMesAnterior.length - 1] : null;

        kmsInicio = Number(ultimoMesAnterior?.kms ?? 0);

        const litrosUltimaLinhaMesAnterior = Number(ultimoMesAnterior?.qtd ?? 0);

        quantidadeTotal =
          (Number.isFinite(litrosUltimaLinhaMesAnterior) ? litrosUltimaLinhaMesAnterior : 0) +
          eventos.slice(0, -1).reduce((acc, x) => {
            const q = Number(x?.qtd ?? 0);
            return acc + (Number.isFinite(q) ? q : 0);
          }, 0);
      }

      const kmsFim = Number(ultima.kms ?? 0);
      const diff = kmsFim - kmsInicio;

      if (!Number.isFinite(kmsInicio) || kmsInicio <= 0 || !Number.isFinite(diff) || diff <= 0) {
        res[mes] = { consumo100: null };
        continue;
      }

      if (!Number.isFinite(quantidadeTotal) || quantidadeTotal <= 0) {
        res[mes] = { consumo100: null };
        continue;
      }

      res[mes] = {
        kmsInicio,
        kmsFim,
        kmsTotal: diff,
        quantidadeTotal,
        consumo100: (quantidadeTotal / diff) * 100,
      };
    }

    return res;
  }, [data]);

  const renderAfterRow = ({ row, rowIndex, rows: tableRows, visibleColumns }) => {
    const ev = row.original;

    const mesAtual = getMesKey(ev?.data_evento);
    const proxEv = rowIndex < tableRows.length - 1 ? tableRows[rowIndex + 1].original : null;
    const proxMes = getMesKey(proxEv?.data_evento);

    const fechaMes = mesAtual && mesAtual !== proxMes;

    if (!fechaMes) return null;

    const consumoMes = mesAtual ? consumoMensalMap[mesAtual] : null;

    return (
      <tr>
        <td
          colSpan={visibleColumns.length}
          style={{
            padding: "8px 10px",
            borderTop: "1px solid #dfe3ea",
            background: "#f8f9fb",
            fontSize: 12,
            color: "#344767",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {mesAtual} — L/kWh 100kms —{" "}
          {consumoMes?.consumo100 && Number.isFinite(consumoMes.consumo100)
            ? fmtConsumo100(consumoMes.consumo100)
            : "—"}{" "}
          ({fmtKmsCalc(consumoMes?.kmsTotal)} kms, {fmtLitrosCalc(consumoMes?.quantidadeTotal)} L)
        </td>
      </tr>
    );
  };

  const consumoHistorico = useMemo(() => {
    return calcularConsumoHistorico(data);
  }, [data]);

  const totais = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];

    const totalMinEvento = arr.reduce((acc, ev) => {
      const hhmm = ev?.duracao_h ? String(ev.duracao_h) : qhToHHmm(ev?.duracao_qh);
      return acc + hhmmToMinutes(hhmm);
    }, 0);

    const totalMinImob = arr.reduce((acc, ev) => {
      // se vier string
      if (ev?.imobilizacao_h) return acc + hhmmToMinutes(String(ev.imobilizacao_h));
      // se vier qh
      return acc + qhToMinutes(ev?.imobilizacao_qh);
    }, 0);

    const totalValor = arr.reduce((acc, ev) => {
      const n = Number(ev?.valor ?? 0);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);

    return {
      totalMinEvento,
      totalMinImob,
      totalValor,
    };
  }, [data]);

  useEffect(() => {
    if (!open) abriuDeepLinkRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!openEdit && pendentePerguntarFuturo && eventoBaseFuturo) {
      setOpenMarcarFuturo(true);
      setPendentePerguntarFuturo(false);
    }
  }, [openEdit, pendentePerguntarFuturo, eventoBaseFuturo]);

  return (
    <>
      {/* DIALOG PRINCIPAL (LISTA) */}
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperComponent={PaperComponent}>
        <DialogTitle className="dialog-drag-handle" sx={{ cursor: "move", pb: 1 }}>
          <MDBox display="flex" flexDirection="column" gap={0.25}>
            <MDTypography
              variant="h4"
              sx={{ color: "#344767", fontWeight: "bold", lineHeight: 1.2 }}
            >
              Eventos — {equipamento?.nome_curto ? String(equipamento.nome_curto) : "Equipamento"}
              {String(equipamento?.matricula ?? "").trim()
                ? ` (${String(equipamento.matricula).trim()})`
                : ""}
            </MDTypography>

            <MDTypography variant="caption" sx={{ color: "#7b809a", lineHeight: 1.1 }}>
              {idEquipamento ? `ID Equipamento: ${idEquipamento}` : ""}
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          {loading && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            mt={0.5}
            gap={2}
            flexWrap="wrap"
          >
            <MDTypography variant="button" sx={{ color: "#344767" }}>
              Média histórica L/kWh 100kms:{" "}
              {consumoHistorico?.consumo100 && Number.isFinite(consumoHistorico.consumo100)
                ? fmtConsumoHistorico100(consumoHistorico.consumo100)
                : "—"}{" "}
              ({fmtKmsCalc(consumoHistorico?.kmsTotal)} kms,{" "}
              {fmtLitrosCalc(consumoHistorico?.quantidadeTotal)} L)
            </MDTypography>

            <MDButton
              color="success"
              disabled={!temPermissao("Menu Equipamentos", "w")}
              onClick={handleOpenNovo}
            >
              Novo Evento
            </MDButton>
          </MDBox>

          <ProjectTable
            tableKey="equipamento-eventos"
            version="v1"
            columns={columns}
            data={data}
            loading={loading}
            rowActions={rowActions}
            emptyMessage="Sem eventos para este equipamento."
            minWidth={1000}
            maxHeight="50vh"
            renderAfterRow={renderAfterRow}
            showKeepFilters
          />
          <MDBox mt={1.5} display="flex" justifyContent="flex-end" gap={3} flexWrap="wrap">
            <MDTypography variant="button">
              Total Duração Eventos: {minutesToHHmm(totais.totalMinEvento)}
            </MDTypography>

            <MDTypography variant="button">
              Total Duração Imobilização: {minutesToHHmm(totais.totalMinImob)}
            </MDTypography>

            <MDTypography variant="button">Total Valor: {fmtMoney(totais.totalValor)}</MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={onClose} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openInfo}
        onClose={handleCloseInfo}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="dialog-drag-handle" sx={{ cursor: "move" }}>
          Detalhe do Evento
        </DialogTitle>

        <DialogContent dividers>
          <MDBox display="grid" gap={2} mt={0.5}>
            <MDBox
              sx={{
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 2,
                p: 2,
                background: "#fafbfd",
              }}
            >
              <MDTypography variant="h6" sx={{ color: "#344767", fontWeight: "bold", mb: 1.5 }}>
                Geral
              </MDTypography>

              <MDBox
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "1fr 1fr",
                }}
                gap={1.25}
              >
                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Data:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {eventoInfo?.data_evento ? String(eventoInfo.data_evento).slice(0, 10) : "—"}
                  </MDTypography>
                </div>

                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Data limite:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {eventoInfo?.data_limite ? String(eventoInfo.data_limite).slice(0, 10) : "—"}
                  </MDTypography>
                </div>

                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Tipo:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {eventoInfo?.tipo_evento_nome_curto || "—"}
                  </MDTypography>
                </div>

                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Estado:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {eventoInfo?.estado || "—"}
                  </MDTypography>
                </div>

                {isViatura && (
                  <div>
                    <MDTypography variant="button" sx={{ color: "#344767" }}>
                      KMs:
                    </MDTypography>
                    <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                      {eventoInfo?.kms_atuais ?? "—"}
                    </MDTypography>
                  </div>
                )}

                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Duração evento:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {eventoInfo?.duracao_h
                      ? String(eventoInfo.duracao_h)
                      : qhToHHmm(eventoInfo?.duracao_qh) || "00:00"}
                  </MDTypography>
                </div>

                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Duração imobilização:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {eventoInfo?.imobilizacao_h
                      ? String(eventoInfo.imobilizacao_h)
                      : qhToHHmm(eventoInfo?.imobilizacao_qh) || "00:00"}
                  </MDTypography>
                </div>

                <div>
                  <MDTypography variant="button" sx={{ color: "#344767" }}>
                    Valor:
                  </MDTypography>
                  <MDTypography variant="button" sx={{ ml: 0.5, fontWeight: "regular" }}>
                    {fmtMoney(eventoInfo?.valor)}
                  </MDTypography>
                </div>
              </MDBox>

              <MDBox mt={1.5}>
                <MDTypography variant="button" sx={{ color: "#344767", display: "block", mb: 0.5 }}>
                  Título
                </MDTypography>

                <MDBox
                  sx={{
                    border: "1px dashed rgba(0,0,0,0.2)",
                    borderRadius: 1.25,
                    p: 1.25,
                    color: "#344767",
                    fontSize: 14,
                  }}
                >
                  {eventoInfo?.titulo || "(sem título)"}
                </MDBox>
              </MDBox>

              <MDBox mt={1.5}>
                <MDTypography variant="button" sx={{ color: "#344767", display: "block", mb: 0.5 }}>
                  Descrição
                </MDTypography>

                <MDBox
                  sx={{
                    border: "1px dashed rgba(0,0,0,0.2)",
                    borderRadius: 1.25,
                    p: 1.25,
                    color: "#344767",
                    fontSize: 14,
                    minHeight: 56,
                  }}
                >
                  {eventoInfo?.descricao || "(sem descrição)"}
                </MDBox>
              </MDBox>
            </MDBox>

            <MDBox
              sx={{
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 2,
                p: 2,
                background: "#fff",
              }}
            >
              <MDTypography variant="h6" sx={{ color: "#344767", fontWeight: "bold", mb: 1.5 }}>
                Linhas do Evento
              </MDTypography>

              {loadingInfo ? (
                <MDBox py={2} display="flex" justifyContent="center">
                  <CircularProgress size={28} />
                </MDBox>
              ) : linhasInfo.length === 0 ? (
                <MDTypography variant="button" sx={{ color: "#7b809a" }}>
                  Sem linhas registadas.
                </MDTypography>
              ) : (
                <>
                  <MDBox sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            Descritivo
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              borderBottom: "1px solid #eee",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Tipo
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              borderBottom: "1px solid #eee",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Qtd.
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              borderBottom: "1px solid #eee",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Preço Unit.
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              borderBottom: "1px solid #eee",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Total
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {linhasInfo.map((ln) => (
                          <tr key={ln.id}>
                            <td
                              style={{
                                padding: "8px",
                                borderTop: "1px solid #f1f1f1",
                                fontSize: 13,
                                color: "#344767",
                              }}
                            >
                              {ln?.descritivo || ""}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop: "1px solid #f1f1f1",
                                fontSize: 13,
                                color: "#344767",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {ln?.tipo_linha || "—"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop: "1px solid #f1f1f1",
                                fontSize: 13,
                                color: "#344767",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {ln?.quantidade ?? ""}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop: "1px solid #f1f1f1",
                                fontSize: 13,
                                color: "#344767",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fmtMoney(ln?.preco_unitario)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop: "1px solid #f1f1f1",
                                fontSize: 13,
                                color: "#344767",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fmtMoney(calcLinhaTotal(ln))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </MDBox>

                  <MDBox mt={1.25} display="flex" justifyContent="flex-end">
                    <MDTypography variant="button" sx={{ color: "#344767" }}>
                      Total Linhas:{" "}
                      {fmtMoney(linhasInfo.reduce((acc, ln) => acc + calcLinhaTotal(ln), 0))}
                    </MDTypography>
                  </MDBox>
                </>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseInfo} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* DIALOG NOVO/EDITAR EVENTO */}
      <Dialog
        open={openEdit}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="dialog-drag-handle" sx={{ cursor: "move" }}>
          {editando ? "Editar Evento" : "Novo Evento"}
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="button" sx={{ display: "block", mb: 1.5 }}>
            {Number(form?.id || 0) > 0 ? `ID Evento: ${form.id}` : ""}
          </MDTypography>

          <MDBox display="grid" gap={1.25} mt={0.75}>
            <MDBox
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "1fr 1fr",
              }}
              gap={1.25}
            >
              {/* Tipo de evento (Autocomplete) */}
              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Tipo de evento *
                </MDTypography>

                <Autocomplete
                  options={(tiposEventos || []).filter((t) => (t?.inativo ?? 0) !== 1)}
                  filterOptions={createAutocompleteFilter((opt) => opt?.nome_curto)}
                  value={
                    (tiposEventos || []).find(
                      (t) => Number(t.id) === Number(form.id_tipo_evento)
                    ) || null
                  }
                  onChange={(_, opt) => setForm((p) => ({ ...p, id_tipo_evento: opt?.id ?? 0 }))}
                  getOptionLabel={(opt) => String(opt?.nome_curto ?? "")}
                  isOptionEqualToValue={(opt, val) => Number(opt?.id) === Number(val?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Escreva para pesquisar..."
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
            </MDBox>

            {/* Datas e campos — labels por cima (sem sobreposição) */}
            <MDBox
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "1fr 1fr",
              }}
              gap={1.25}
            >
              <MDInput
                label="Título"
                value={form.titulo || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, titulo: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />

              <MDInput
                label="Descrição"
                value={form.descricao || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, descricao: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
                multiline
                rows={3}
              />

              <MDBox
                sx={{
                  gridColumn: "1 / -1",
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 1.25,
                  p: 1.25,
                  background: "rgba(0,0,0,0.02)",
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1, color: "#344767" }}>
                  Evento
                </MDTypography>
                <MDBox
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    sm: "1fr 1fr",
                  }}
                  gap={1.25}
                >
                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      Estado
                    </MDTypography>

                    <Autocomplete
                      options={ESTADOS_EVENTO}
                      filterOptions={createAutocompleteFilter((opt) => opt)}
                      value={ESTADOS_EVENTO.includes(form.estado) ? form.estado : "Não iniciado"}
                      inputValue={estadoInput}
                      onInputChange={(_, v) => setEstadoInput(v)}
                      onChange={(_, v) => {
                        const novo = v || "Não iniciado";

                        setForm((p) => ({ ...p, estado: novo }));
                        setEstadoInput(novo);
                      }}
                      disableClearable
                      clearOnBlur
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          onBlur={() => {
                            // se o user escreveu algo e saiu sem selecionar, volta ao valor válido
                            const atual = ESTADOS_EVENTO.includes(form.estado)
                              ? form.estado
                              : "Não iniciado";
                            setEstadoInput(atual);
                          }}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      Data
                    </MDTypography>

                    <MDBox display="flex" alignItems="center" gap={0.75}>
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={form.data_evento || ""}
                        onChange={(e) => setForm((p) => ({ ...p, data_evento: e.target.value }))}
                      />

                      <IconButton
                        size="small"
                        title="Colocar hoje"
                        onClick={() =>
                          setForm((p) => ({
                            ...p,
                            data_evento: new Date().toISOString().slice(0, 10),
                          }))
                        }
                      >
                        {/* reutiliza um ícone já presente no teu ficheiro (sem novos imports) */}
                        <TodayIcon fontSize="small" />
                      </IconButton>
                    </MDBox>
                  </div>

                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      Data limite
                    </MDTypography>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={form.data_limite || ""}
                      onChange={(e) => setForm((p) => ({ ...p, data_limite: e.target.value }))}
                    />
                  </div>

                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      KMs
                    </MDTypography>

                    <TextField
                      size="small"
                      fullWidth
                      value={form.kms_atuais ?? ""}
                      onChange={(e) => setForm((p) => ({ ...p, kms_atuais: e.target.value }))}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </div>
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  gridColumn: "1 / -1",
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 1.25,
                  p: 1.25,
                  background: "rgba(0,0,0,0.02)",
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1, color: "#344767" }}>
                  Mão de obra
                </MDTypography>

                <MDBox
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    sm: "1fr 1fr",
                  }}
                  gap={1.25}
                >
                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      Duração (HH:mm)
                    </MDTypography>

                    <Autocomplete
                      options={DURACOES}
                      filterOptions={createAutocompleteFilter((opt) => opt)}
                      value={form.duracao_h || "00:15"}
                      onChange={(_, v) => setForm((p) => ({ ...p, duracao_h: v || "00:15" }))}
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} placeholder="00:15" size="small" fullWidth />
                      )}
                    />
                  </div>

                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Number(form.mao_obra_interna ?? 0) === 1}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, mao_obra_interna: e.target.checked ? 1 : 0 }))
                          }
                        />
                      }
                      label="Mão de obra interna"
                    />
                  </div>
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  gridColumn: "1 / -1",
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 1.25,
                  p: 1.25,
                  background: "rgba(0,0,0,0.02)",
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1, color: "#344767" }}>
                  Imobilização
                </MDTypography>

                <MDBox
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    sm: "1fr 1fr",
                  }}
                  gap={1.25}
                >
                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      Imobilização (HH:mm)
                    </MDTypography>

                    <Autocomplete
                      options={["", ...DURACOES]}
                      filterOptions={createAutocompleteFilter((opt) => opt)}
                      value={
                        form.imobilizacao_h && form.imobilizacao_h !== "00:00"
                          ? form.imobilizacao_h
                          : ""
                      }
                      onChange={(_, v) => setForm((p) => ({ ...p, imobilizacao_h: v ?? "" }))}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="(opcional)" size="small" fullWidth />
                      )}
                    />
                  </div>

                  <div>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                      Custo/hora imobilização do equipamento
                    </MDTypography>

                    <TextField
                      size="small"
                      fullWidth
                      value={form.custo_hora_imobilizacao ?? ""}
                      placeholder="(opcional)"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, custo_hora_imobilizacao: e.target.value }))
                      }
                    />
                  </div>
                </MDBox>
              </MDBox>

              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Valor (calculado)
                </MDTypography>

                <TextField size="small" fullWidth value={form.valor ?? ""} disabled />

                <MDBox mt={0.75}>
                  <MDTypography variant="caption" sx={{ display: "block", color: "#7b809a" }}>
                    Linhas (peças/serviços): {fmtMoney(form.valor_linhas)}
                  </MDTypography>

                  <MDTypography variant="caption" sx={{ display: "block", color: "#7b809a" }}>
                    Mão de obra:{" "}
                    {Number(form.mao_obra_interna ?? 0) === 1 && form.duracao_h
                      ? `${form.duracao_h} × ${fmtMoney(
                          hhmmToHours(form.duracao_h) > 0
                            ? Number(form.valor_mao_obra ?? 0) / hhmmToHours(form.duracao_h)
                            : 0
                        )} = ${fmtMoney(form.valor_mao_obra)}`
                      : fmtMoney(form.valor_mao_obra)}
                  </MDTypography>

                  <MDTypography variant="caption" sx={{ display: "block", color: "#7b809a" }}>
                    Imobilização:{" "}
                    {form.imobilizacao_h
                      ? `${form.imobilizacao_h} × ${fmtMoney(
                          hhmmToHours(form.imobilizacao_h) > 0
                            ? Number(form.valor_imobilizacao ?? 0) /
                                hhmmToHours(form.imobilizacao_h)
                            : 0
                        )} = ${fmtMoney(form.valor_imobilizacao)}`
                      : fmtMoney(form.valor_imobilizacao)}
                  </MDTypography>

                  <MDTypography
                    variant="caption"
                    sx={{ display: "block", color: "#1a2b4c", mt: 0.5 }}
                  >
                    Total: {fmtMoney(form.valor)}
                  </MDTypography>
                </MDBox>
              </div>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            gap: 1,
            flexWrap: "wrap",
            justifyContent: {
              xs: "center",
              sm: "flex-end",
            },
          }}
        >
          <MDButton onClick={handleCloseEdit} color="secondary">
            Cancelar
          </MDButton>

          <MDButton
            color="info"
            onClick={handleOpenAnexosEvento}
            disabled={!Number(form?.id ?? 0) || !temPermissao("Menu Equipamentos", "u")}
          >
            Anexos
          </MDButton>

          <MDButton
            color="info"
            onClick={() => handleOpenLinhas({ ...form, id: Number(form?.id ?? 0) })}
            disabled={!Number(form?.id ?? 0) || !temPermissao("Menu Equipamentos", "u")}
          >
            Linhas do Evento
          </MDButton>

          <MDButton
            onClick={handleSave}
            color="success"
            disabled={
              editando
                ? !temPermissao("Menu Equipamentos", "u")
                : !temPermissao("Menu Equipamentos", "w")
            }
          >
            {editando ? "Guardar" : "Inserir"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMarcarFuturo}
        onClose={() => setOpenMarcarFuturo(false)}
        maxWidth="sm"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="dialog-drag-handle" sx={{ cursor: "move" }}>
          Marcar evento futuro?
        </DialogTitle>

        <DialogContent>
          <MDBox mt={1.5} display="grid" gap={1.25}>
            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Nova data do evento
              </MDTypography>

              <TextField
                type="date"
                size="small"
                fullWidth
                value={novoEventoData}
                onChange={(e) => setNovoEventoData(e.target.value)}
              />
            </div>

            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Nova data limite
              </MDTypography>

              <TextField
                type="date"
                size="small"
                fullWidth
                value={novoEventoLimite}
                onChange={(e) => setNovoEventoLimite(e.target.value)}
              />
            </div>

            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Novos KMs
              </MDTypography>

              <TextField
                type="number"
                size="small"
                fullWidth
                value={novoEventoKms}
                onChange={(e) => setNovoEventoKms(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
              />
            </div>

            <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
              Será criado um novo evento idêntico a este, com o estado “Não iniciado”. Deve indicar
              a nova data do evento e/ou os KMs a que o evento deverá acontecer.
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ gap: 1 }}>
          <MDButton
            onClick={() => {
              setOpenMarcarFuturo(false);
              setEventoBaseFuturo(null);
              setNovoEventoData("");
              setNovoEventoLimite("");
              setNovoEventoKms("");
              setPendentePerguntarFuturo(false);
            }}
            color="secondary"
          >
            Não Marcar
          </MDButton>

          <MDButton onClick={marcarEventoFuturo} color="success">
            Marcar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmDel}
        onClose={() => setOpenConfirmDel(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar eliminação</DialogTitle>

        <DialogContent>
          <MDTypography variant="body2">Apagar este evento?</MDTypography>

          {eventoParaApagar?.titulo && (
            <MDTypography variant="button" sx={{ display: "block", mt: 1 }}>
              {eventoParaApagar.titulo}
            </MDTypography>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenConfirmDel(false)} color="secondary">
            Cancelar
          </MDButton>

          <MDButton onClick={confirmarApagarEvento} color="error">
            Apagar
          </MDButton>
        </DialogActions>
      </Dialog>

      <DialogEquipamentoEventoLinhas
        open={openLinhas}
        onClose={handleCloseLinhas}
        evento={eventoLinhas}
        onChanged={() => {
          // recarrega eventos para o VALOR refletir novo total
          carregarEventos();
        }}
      />

      <DialogEquipamentoFicheiros
        open={openAnexosEvento}
        onClose={() => setOpenAnexosEvento(false)}
        idEquipamento={idEquipamento}
        nomeEquipamento={equipamento?.nome_curto || equipamento?.designacao || ""}
        acesso="Menu Equipamentos"
        subpastas={["Eventos"]}
        mostrarSelectorSubpasta={false}
        idevento={Number(form?.id || 0)}
        titulo={`Anexos do Evento${Number(form?.id || 0) > 0 ? ` ${form.id}` : ""}`}
      />
    </>
  );
}
